import React from 'react';
import { ArrowLeft, Calendar } from 'lucide-react';
import "./InsightCard2.css"; // Using the same CSS file
import Navbar from '../Navbar';
import Footer from '../Footer';
import contributerImg from "../../assets/Team/Holt_ Spencer.jpg"

const InsightCard2 = () => {
  const handleBackClick = () => {
    window.location.href = '/insights';
  };

  return (
    <>
      <Navbar />
      
      <div className="insights-card1-detail-container">
        {/* Header Section */}
        <header className="insights-card1-header-section">
          <div className="insights-card1-header-overlay"></div>
          <div className="insights-card1-header-content">
            <button onClick={handleBackClick} className="insights-card1-back-button">
              <ArrowLeft size={20} />
              <span>Back to Insights</span>
            </button>
            <div className="insights-card1-header-text">
              <h1 className="insights-card1-main-title">
                Rethinking Finance Functions in High-Growth Companies
              </h1>
              <div className="insights-card1-article-meta">
                <Calendar size={16} />
                <span>Published on 21 July 2025</span>
              </div>
            </div>
          </div>
        </header>

        {/* Article Content */}
        <article className="insights-card1-article-content">
          <div className="insights-card1-content-wrapper">
             <div className="insights-card1-navigation-section">
              <a href="/insights/rethinking-finance-part-1" className="insights-card1-part-link">
                Read Part 1: Building a Finance Function That Enables Growth  →
              </a>
            </div>
            <div className="insights-card1-subtitle">
              Part 2 - Structure finance to fit your unique business model, not someone else's
            </div>

           

            <div className="insights-card1-intro-paragraph">
              Years ago, I studied piano performance and pedagogy under Dr. Paul Pollei, a distinguished professor and founder of the Gina Bachauer International Piano Foundation. Once, when discussing effective learning methods, he asked me a curious question: "Do you know the most common piano method used by beginning students?" The answer was "John Thompson."
            </div>

            <p>
              The choice of the John Thompson method—though not the most advanced or pedagogically progressive—was driven by its familiarity. It wasn't the best; it was the best-known. Parents and aspiring students often selected local teachers based on proximity and convenience, rarely equipped with the knowledge to assess teaching methodology or instructional quality. These teachers, many of whom are talented musicians, often lacked formal pedagogical training themselves. They taught what they had been taught—creating a self-perpetuating loop of tradition rather than innovation.
            </p>

            <p>
              The result? A broad but shallow base of musical education. Capable students learned to play, yes—but few achieved mastery. Not because of lack of talent, but because of a system optimized for familiarity over excellence.
            </p>

            <hr className="insights-card1-section-divider" />

            <section className="insights-card1-content-section">
              <h2 className="insights-card1-section-title">
                The Business Parallel: Familiarity in the Finance Function
              </h2>

              <p>
                The same pattern occurs in the finance functions of early-stage and rapidly scaling businesses. Rather than designing a finance operation intentionally from the ground up, teams frequently recreate what they've seen before. Reporting formats, KPI dashboards, monthly close procedures, even budgeting workflows—these are often copied from prior companies, which themselves may have inherited them in the same way.
              </p>

              <p>
                This approach feels safe. It's fast. It minimizes decision fatigue in the face of other growing pains. But much like the piano student on a plateau, the business risks mediocrity by failing to question whether these inherited practices are right for their unique context.
              </p>
            </section>

            <hr className="insights-card1-section-divider" />

            <section className="insights-card1-content-section">
              <h2 className="insights-card1-section-title">
                The Cost of Copy-Paste Finance
              </h2>

              <p>
                Relying on familiar systems introduces a subtle but significant opportunity cost. Poorly tailored financial systems result in:
              </p>

              <div className="insights-card1-emphasis-section">
                <ul className="insights-card1-bullet-list">
                  <li><strong>Misaligned KPIs:</strong> Industry-standard metrics may be valuable to external stakeholders, but if they don't align with company-specific strategic goals, they bely the company's failure to understand and build their own business.</li>
                  <li><strong>Operational Drag:</strong> Adopted processes often require workarounds and manual interventions, draining time and morale from high-value finance talent as well as talent from other functions.</li>
                  <li><strong>Impaired Decision-Making:</strong> Reports optimized for past organizations may obscure the key insights leaders need in a new environment.</li>
                </ul>
              </div>

              <p>
                The cumulative effect is a finance organization that looks competent but lacks strategic leverage. And in high-growth settings, this drag compounds rapidly.
              </p>
            </section>

            <hr className="insights-card1-section-divider" />

            <section className="insights-card1-content-section">
              <h2 className="insights-card1-section-title">
                A Better Approach: Design from Principles
              </h2>

              <p>
                Rather than defaulting to precedent, leaders should design their function based on first principles of governance, transparency, and business alignment. This approach includes:
              </p>

              <div className="insights-card1-emphasis-section">
                <ul className="insights-card1-bullet-list">
                  <li><strong>Strategic KPI Development:</strong> Start with company goals, then work backward to define what success should look like. Adopt industry-standard KPIs selectively, not reflexively. It is painfully obvious when standard KPIs are shoehorned into a unique and creative business.</li>
                  <li><strong>Custom Process Engineering:</strong> Build processes that scale. Document procedures early. If you are struggling to define the procedures and processes, then you likely have some more work to do in discovering your customers and building your business accordingly.</li>
                  <li><strong>Leverage Systems:</strong> As you are building your business processes, match your finance processes and procedures to the systems, and build your reporting structures upon that foundation.</li>
                </ul>
              </div>

              <p>
                By intentionally crafting these systems, the finance function becomes not just a service department—but a strategic partner.
              </p>
            </section>

            <hr className="insights-card1-section-divider" />

            <section className="insights-card1-content-section">
              <p className="insights-card1-closing-paragraph">
                The lesson I learned from piano pedagogy is clear: Doing what's familiar can feel like progress, but it often caps potential. Excellence demands intention. Finance leaders in high-growth companies must resist the comfort of copy-paste methods and instead design and build systems that reflect their unique mission and context.
              </p>

              <div className="insights-card1-contributor-block">
                <img
                  className="insights-card1-contributor-img"
                  src={contributerImg}
                  alt="Spencer Holt"
                />
                <div className="insights-card1-contributor-details">
                  <h4 className="insights-card1-contributor-title">About the Contributor</h4>
                  <p className="insights-card1-contributor-bio">
                    <strong>Spencer Holt</strong> is a lifelong accounting and finance professional of VC and PE portfolio companies. He leads AlphaXN's fractional CFO practice.
                  </p>
                </div>
              </div>
            </section>

          </div>
        </article>
      </div>
      <Footer />
    </>
  );
};

export default InsightCard2;